import { useCallback, useEffect, useState } from 'react';
import { Loader2, Landmark, Play, Settings, AlertTriangle, XCircle, CheckCircle2, PowerOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { pennylaneBankFeedAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { PennylaneBankFeedConfigureModal } from './PennylaneBankFeedConfigureModal';

const STATUS_LABEL = {
  ok: 'OK',
  failed: 'Failed',
  in_progress: 'Running',
};

const STATUS_VARIANT = {
  ok: 'default',
  failed: 'destructive',
  in_progress: 'outline',
};

function MappingRow({ mapping, onRunNow, onDeactivate, running }) {
  const lastRunStatus = mapping.last_run_status;

  return (
    <div className="border rounded-lg px-3 py-3 space-y-2" data-testid="pl-bankfeed-mapping-row">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <span className="text-sm font-medium">{mapping.entity_name}</span>
          <span className="text-xs text-muted-foreground ml-2">
            Pennylane account <code>{mapping.pennylane_account_id}</code>
            {' -> '}
            {mapping.internal_account_name || mapping.internal_bank_account_id}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {lastRunStatus && (
            <Badge variant={STATUS_VARIANT[lastRunStatus] || 'outline'} className="text-[10px]" data-testid="pl-bankfeed-status-badge">
              {STATUS_LABEL[lastRunStatus] || lastRunStatus}
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRunNow(mapping.mapping_id)}
            disabled={running}
            data-testid={`pl-bankfeed-run-now-${mapping.mapping_id}`}
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
            Run Now
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDeactivate(mapping.mapping_id)}
            data-testid={`pl-bankfeed-deactivate-${mapping.mapping_id}`}
          >
            <PowerOff className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
        <div>
          <div className="font-medium text-foreground">Last run</div>
          <div>{mapping.last_run_at ? new Date(mapping.last_run_at).toLocaleString() : 'never'}</div>
        </div>
        <div>
          <div className="font-medium text-foreground">Ingested (last / total)</div>
          <div>{mapping.last_run_inserted ?? 0} / {mapping.total_ingested ?? 0}</div>
        </div>
        <div>
          <div className="font-medium text-foreground">Cursor (max id)</div>
          <div>{mapping.cursor_max_id ?? '-'}</div>
        </div>
        <div>
          <div className="font-medium text-foreground">Latest transaction date</div>
          <div>{mapping.latest_transaction_date || '-'}</div>
        </div>
      </div>

      {mapping.empty_feed_warning && (
        <div
          className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1.5"
          data-testid="pl-bankfeed-empty-warning"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Connected and syncing, but zero transactions ingested so far.
        </div>
      )}

      {lastRunStatus === 'failed' && mapping.last_run_error && (
        <div className="flex items-center gap-2 text-xs text-red-600" data-testid="pl-bankfeed-run-error">
          <XCircle className="h-3.5 w-3.5 shrink-0" />
          {mapping.last_run_error}
        </div>
      )}

      {!mapping.backfill_complete && (
        <div
          className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1.5"
          data-testid="pl-bankfeed-pagination-warning"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Historical backfill is still in progress; older history may be
          incomplete. Run Now again to continue -- this clears once the
          full history has genuinely been walked.
        </div>
      )}

      {lastRunStatus === 'ok' && mapping.backfill_complete && !mapping.empty_feed_warning && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          Feed is healthy.
        </div>
      )}
    </div>
  );
}

export function PennylaneBankFeedStatusPanel() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningMappingId, setRunningMappingId] = useState(null);
  const [configureOpen, setConfigureOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await pennylaneBankFeedAPI.listMappings();
      setMappings(data.mappings || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (mappingId) => {
    setRunningMappingId(mappingId);
    try {
      const { data } = await pennylaneBankFeedAPI.runNow(mappingId);
      const run = data.run || {};
      if (run.status === 'ok') {
        toast.success(`Run complete: ${run.inserted ?? 0} inserted, ${run.duplicates ?? 0} duplicates.`);
      } else {
        toast.error(`Run failed: ${run.error || 'unknown error'}`);
      }
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunningMappingId(null);
    }
  };

  const deactivate = async (mappingId) => {
    try {
      await pennylaneBankFeedAPI.deactivateMapping(mappingId);
      toast.success('Mapping deactivated.');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Landmark className="h-5 w-5" />
          Pennylane Bank Feed Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Connection mappings from a Pennylane bank account to an internal BR OS
          bank account, with daily sync status. One active feed per internal
          account is enforced at mapping time.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading mappings...
          </div>
        ) : mappings.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="pl-bankfeed-no-mappings">
            No Pennylane bank feed mappings configured yet.
          </p>
        ) : (
          <div className="space-y-2" data-testid="pl-bankfeed-mapping-list">
            {mappings.map((m) => (
              <MappingRow
                key={m.mapping_id}
                mapping={m}
                onRunNow={runNow}
                onDeactivate={deactivate}
                running={runningMappingId === m.mapping_id}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setConfigureOpen(true)} data-testid="pl-bankfeed-configure-open">
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Configure
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </CardContent>

      <PennylaneBankFeedConfigureModal
        open={configureOpen}
        onOpenChange={setConfigureOpen}
        onSaved={load}
      />
    </Card>
  );
}
